import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { AffordabilityBarsCard } from '../components/analyses/AffordabilityBarsCard';
import { ScenarioComparisonCard } from '../components/analyses/ScenarioComparisonCard';
import { useProfileDetail, useProfiles } from '../hooks/useProfiles';
import { usePropertyDetail } from '../hooks/useProperties';
import { useAnalyses } from '../hooks/useAnalyses';
import { useOffers } from '../hooks/useOffers';
import { getDefaultInputs, calculateAll } from '../lib/calculator';
import { getCashIndicator, getPaymentIndicator } from '../lib/price-levels';
import { apiFetch } from '../lib/api';
import { fmtDollarInt, fmtDollar } from '../lib/format';
import type { CalculatorInputs, Offer, CreditScoreRange, Property, Analysis } from '../lib/types';

// When there's a co-borrower, lenders use the lower of the two middle credit scores
function lowerCreditScore(a: CreditScoreRange, b: CreditScoreRange): CreditScoreRange {
  return (parseInt(a) <= parseInt(b) ? a : b) as CreditScoreRange;
}


import { OfferStatusBadge } from '../components/ui/OfferStatusBadge';

function OffersList({ offers, profileId, propertyId }: { offers: Offer[]; profileId: string; propertyId: string }) {
  if (offers.length === 0) return null;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>Offer Plans</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {offers.map(offer => (
          <Link
            key={offer.id}
            to={`/profiles/${profileId}/properties/${propertyId}/offers/${offer.id}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}>
              <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{fmtDollarInt(offer.offerPrice)}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {fmtDollar(offer.results.totalMonthly)}/mo · C2C {fmtDollarInt(offer.results.cashToClose.cashToClose)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <OfferStatusBadge status={offer.status} />
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {new Date(offer.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const ANALYSES_PAGE_SIZE = 3;

function AnalysesList({
  analyses,
  offers,
  profileId,
  propertyId,
  onPlanOffer,
  onDelete,
}: {
  analyses: Analysis[];
  offers: Offer[];
  profileId: string;
  propertyId: string;
  onPlanOffer: (analysis: Analysis) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (analyses.length === 0) {
    return (
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
        Tip: Click "Open Full Calculator" or any "Details" button to save a default analysis — you can adjust all inputs and save custom scenarios there.
      </p>
    );
  }

  const visible = expanded ? analyses : analyses.slice(0, ANALYSES_PAGE_SIZE);
  const hiddenCount = analyses.length - ANALYSES_PAGE_SIZE;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>Saved Analyses</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {visible.map((analysis, i) => {
          const verdictClass = analysis.results.affordability?.verdictClass ?? 'verdict-bad';
          const verdictText = analysis.results.affordability?.verdictText ?? 'Affordability unknown';
          const indicatorColor =
            verdictClass === 'verdict-good' ? 'green' :
            verdictClass === 'verdict-ok' || verdictClass === 'verdict-stretch' ? 'yellow' :
            'red';
          const hasOffer = offers.some(o => o.analysisId === analysis.id);
          return (
            <div key={analysis.id} className="card">
              <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700, fontSize: '0.95rem' }}>
                      <span
                        className={`indicator indicator-${indicatorColor}`}
                        style={{ flexShrink: 0, marginRight: 0 }}
                        title={verdictText}
                      />
                      {fmtDollarInt(analysis.askingPrice)}
                      {i === 0 && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 600 }}>
                          latest
                        </span>
                      )}
                      {hasOffer && (
                        <span className="analysis-offer-badge" title="Has an offer plan">Offer</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {fmtDollar(analysis.results.totalMonthly)}/mo · C2C {fmtDollarInt(analysis.results.cashToClose.cashToClose)}
                      {' · '}
                      {new Date(analysis.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="analysis-actions">
                    <Link
                      to={`/profiles/${profileId}/properties/${propertyId}/analyses/${analysis.id}`}
                      className="btn btn-sm btn-secondary"
                    >
                      View
                    </Link>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onPlanOffer(analysis)}
                    >
                      Plan Offer
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        if (window.confirm('Delete this analysis?')) onDelete(analysis.id);
                      }}
                      style={{ color: 'var(--red)' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setExpanded(e => !e)}
          style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

function PropertyContent() {
  const { profileId, propertyId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationBanner = (location.state as { banner?: string } | null)?.banner ?? null;

  const { profile } = useProfileDetail(profileId, getToken);
  const { profiles: allProfiles } = useProfiles(getToken);
  const { property, loading: propLoading, error: propError, refetch: refetchProperty } = usePropertyDetail(profileId, propertyId, getToken);
  const { analyses, loading: analysesLoading, createAnalysis, deleteAnalysis } = useAnalyses(propertyId, getToken);
  const { offers, loading: offersLoading, createOffer } = useOffers(propertyId, getToken);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const loading = propLoading || analysesLoading || offersLoading;

  // Latest analysis (most recent)
  const latestAnalysis = analyses.length > 0 ? analyses[0] : null;

  // Base inputs for saving a new analysis or computing comparisons.
  // When a saved analysis exists, we use its inputs as the base (preserving user-configured
  // settings like rate, term, loan type, etc.) but always override property-derived fields
  // with current values so that price changes are reflected immediately.
  const baseInputs = useMemo((): CalculatorInputs => {
    const inputs = latestAnalysis ? { ...latestAnalysis.inputs } : getDefaultInputs();
    if (property) {
      // Always reflect the current asking price — never use the frozen snapshot price
      inputs.homePrice = property.askingPrice;
      inputs.downPaymentAmt = Math.round(property.askingPrice * (inputs.downPaymentPct / 100));
      inputs.propertyAddress = property.address;
      inputs.propertyCity = property.city;
      inputs.propertyZip = property.zip;
      if (!latestAnalysis && property.county) inputs.mnCounty = property.county;
    }
    if (!latestAnalysis && profile) {
      // Apply profile defaults only when there's no saved analysis to preserve.
      // When an analysis exists, the user's manually-entered values take precedence.
      const co = profile.coBorrower;
      inputs.grossAnnualIncome = profile.grossAnnualIncome + (co?.grossAnnualIncome ?? 0);
      inputs.creditScore = co
        ? lowerCreditScore(profile.creditScoreRange, co.creditScoreRange)
        : profile.creditScoreRange;
      inputs.monthlyOtherDebt = profile.monthlyDebts + (co?.monthlyDebts ?? 0);
      inputs.use401k = profile.use401k ?? false;
      if (profile.use401k) {
        inputs.loan401kAmount = profile.loan401kAmount ?? inputs.loan401kAmount;
        inputs.loan401kRate = profile.loan401kRate ?? inputs.loan401kRate;
        inputs.loan401kTerm = profile.loan401kTerm ?? inputs.loan401kTerm;
      }
    }
    // Always apply the profile's default down payment percentage — this is a profile-level
    // setting (like asking price on the property) that should stay current even when a
    // saved analysis exists, so the spread table never silently uses a stale snapshot value.
    if (profile?.defaultDownPaymentPct !== undefined) {
      inputs.downPaymentPct = profile.defaultDownPaymentPct;
      inputs.downPaymentAmt = Math.round(inputs.homePrice * (profile.defaultDownPaymentPct / 100));
    }
    return inputs;
  }, [latestAnalysis, property, profile]);

  // True when a saved analysis exists but the profile has been updated since it was saved.
  // In this case the price-level table is still showing frozen income/debts/credit values.
  const isProfileStale = !!(
    latestAnalysis &&
    profile?.updatedAt &&
    profile.updatedAt > latestAnalysis.createdAt
  );

  // (price levels table removed — replaced by AffordabilityBarsCard)

  // Open the full calculator for a given price level.
  // Open the full calculator in a fresh session at asking price.
  const handleOpenCalculator = () => {
    if (!profileId || !propertyId || !property) return;
    navigate(
      `/profiles/${profileId}/properties/${propertyId}/analyses/new?price=${property.askingPrice}`,
      { state: { baseInputs } }
    );
  };

  // Save this property to a different profile and navigate to it.
  // The backend deduplicates by address — if the canonical already exists,
  // the same property ID is returned rather than creating a duplicate.
  const handleAnalyzeWithProfile = async (targetProfileId: string) => {
    if (!property) return;
    const prop = await apiFetch<Property>(`/profiles/${targetProfileId}/properties`, {
      method: 'POST',
      body: JSON.stringify({
        address: property.address,
        city: property.city,
        zip: property.zip,
        county: property.county,
        askingPrice: property.askingPrice,
        notes: property.notes,
      }),
    }, getToken);
    navigate(`/profiles/${targetProfileId}/properties/${prop.id}`);
  };

  const handleArchiveProperty = async () => {
    if (!profileId || !propertyId || !property) return;
    setArchiving(true);
    try {
      await apiFetch(`/profiles/${profileId}/properties/${propertyId}`, {
        method: 'PUT',
        body: JSON.stringify({ archived: !property.archived }),
      }, getToken);
      await refetchProperty();
    } catch (err: any) {
      alert(`Failed to ${property.archived ? 'unarchive' : 'archive'} property: ${err.message}`);
    } finally {
      setArchiving(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (!profileId || !propertyId) return;
    setDeleting(true);
    try {
      await apiFetch(`/profiles/${profileId}/properties/${propertyId}`, { method: 'DELETE' }, getToken);
      navigate(`/profiles/${profileId}`);
    } catch (err: any) {
      alert(`Failed to delete property: ${err.message}`);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Create an offer at asking price. Saves a fresh analysis first so the offer
  // always references an analysis that matches the current property/profile state.
  const handlePlanOffer = async () => {
    if (!property || !profileId) return;

    setSaving(true);
    try {
      // Save a fresh analysis from current base inputs
      const savedResults = calculateAll(baseInputs);
      const saved = await createAnalysis({
        askingPrice: property.askingPrice,
        inputs: baseInputs,
        results: savedResults,
        priceLevels: [],
        profileId,
      });

      // Create the offer at asking price
      const closingDate = new Date();
      closingDate.setDate(closingDate.getDate() + 45);

      const offer = await createOffer({
        analysisId: saved.id,
        offerPrice: property.askingPrice,
        profileId,
        inputs: baseInputs,
        results: savedResults,
        earnestMoney: Math.round(property.askingPrice * 0.01),
        closingDate: closingDate.toISOString().slice(0, 10),
        contingencies: { inspection: true, appraisal: true, financing: true, saleDependency: false },
        notes: '',
        status: 'draft',
      });
      navigate(`/profiles/${profileId}/properties/${propertyId}/offers/${offer.id}`);
    } catch (err: any) {
      alert(`Failed to create offer: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Create an offer directly from a saved analysis (using that analysis's asking price and inputs)
  const handlePlanOfferFromAnalysis = async (analysis: Analysis) => {
    if (!property || !profileId) return;
    const offerPrice = analysis.askingPrice;
    const inputs: CalculatorInputs = {
      ...analysis.inputs,
      homePrice: offerPrice,
      downPaymentAmt: Math.round(offerPrice * (analysis.inputs.downPaymentPct / 100)),
    };
    const results = calculateAll(inputs);
    const closingDate = new Date();
    closingDate.setDate(closingDate.getDate() + 45);
    try {
      const offer = await createOffer({
        analysisId: analysis.id,
        offerPrice,
        profileId,
        inputs,
        results,
        earnestMoney: Math.round(offerPrice * 0.01),
        closingDate: closingDate.toISOString().slice(0, 10),
        contingencies: { inspection: true, appraisal: true, financing: true, saleDependency: false },
        notes: '',
        status: 'draft',
      });
      navigate(`/profiles/${profileId}/properties/${propertyId}/offers/${offer.id}`);
    } catch (err: any) {
      alert(`Failed to create offer: ${err.message}`);
    }
  };

  // Scenario comparison: compute each profile's affordability for this property
  const scenarioRows = useMemo(() => {
    if (!property || allProfiles.length === 0) return [];
    return allProfiles.map(p => {
      const co = p.coBorrower;
      const combinedIncome = p.grossAnnualIncome + (co?.grossAnnualIncome ?? 0);
      const combinedDebts = p.monthlyDebts + (co?.monthlyDebts ?? 0);
      const effectiveCredit = co
        ? lowerCreditScore(p.creditScoreRange, co.creditScoreRange)
        : p.creditScoreRange;
      const scenarioDPPct = p.defaultDownPaymentPct ?? baseInputs.downPaymentPct;
      const profileInputs: CalculatorInputs = {
        ...baseInputs,
        grossAnnualIncome: combinedIncome,
        monthlyCarPayment: 0,
        monthlyStudentLoans: 0,
        monthlyCreditCards: 0,
        monthlyOtherDebt: combinedDebts,
        creditScore: effectiveCredit,
        use401k: p.use401k ?? false,
        loan401kAmount: p.loan401kAmount ?? baseInputs.loan401kAmount,
        loan401kRate: p.loan401kRate ?? baseInputs.loan401kRate,
        loan401kTerm: p.loan401kTerm ?? baseInputs.loan401kTerm,
        downPaymentPct: scenarioDPPct,
        downPaymentAmt: Math.round(property.askingPrice * (scenarioDPPct / 100)),
      };
      const results = calculateAll(profileInputs);
      return {
        profile: p,
        monthlyPayment: results.totalMonthly,
        cashToClose: results.cashToClose.cashToClose,
        frontDTIPct: results.affordability ? results.affordability.frontDti : null,
        payIndicator: getPaymentIndicator(results.totalMonthly, combinedIncome / 12),
        cashIndicator: getCashIndicator(results.cashToClose.cashToClose, p.cashOnHand),
        isCurrentProfile: p.id === profileId,
      };
    });
  }, [allProfiles, baseInputs, property, profileId]);

  if (loading) {
    return (
      <div className="page-container">
        <h2 className="page-title">Property Details</h2>
        <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
      </div>
    );
  }

  if (propError || !property) {
    return (
      <div className="page-container">
        <h2 className="page-title">Property Details</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          {propError || 'Property not found'}
        </div>
        <Link to={`/profiles/${profileId}`} className="btn btn-secondary" style={{ marginTop: '1rem', display: 'inline-block' }}>← Back to Profile</Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Link to={`/profiles/${profileId}`} style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none' }}>
        ← Back to {profile?.name || 'Profile'}
      </Link>

      {locationBanner && (
        <div className="address-detected" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8', marginBottom: '1rem' }}>
          <span className="detect-icon">ℹ️</span>
          <span>{locationBanner}</span>
        </div>
      )}

      {/* Property Header */}
      <div className="property-header">
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>
            {property.address}
            {property.archived && (
              <span style={{
                display: 'inline-block',
                marginLeft: '0.5rem',
                padding: '1px 8px',
                borderRadius: '4px',
                background: '#f1f5f9',
                color: 'var(--text-muted)',
                fontSize: '0.65rem',
                fontWeight: 500,
                verticalAlign: 'middle',
                letterSpacing: '0.03em',
              }}>
                ARCHIVED
              </span>
            )}
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: '0.25rem 0', fontSize: '0.9rem' }}>
            {[property.city, property.county ? `${property.county} County` : '', property.zip].filter(Boolean).join(', ')}
          </p>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
            {fmtDollarInt(property.askingPrice)}
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.5rem' }}>Asking Price</span>
          </div>
          {property.notes && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{property.notes}</div>
          )}
        </div>
        <div className="property-actions">
          <button
            className="btn btn-secondary"
            onClick={handleOpenCalculator}
            disabled={deleting}
          >
            Open Full Calculator →
          </button>
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-secondary property-actions-menu-btn"
              onClick={() => setShowActionsMenu(m => !m)}
              disabled={archiving || deleting}
              title="More actions"
              aria-label="More actions"
            >
              ···
            </button>
            {showActionsMenu && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                  onClick={() => setShowActionsMenu(false)}
                />
                <div className="property-actions-dropdown">
                  <button
                    className="property-actions-dropdown-item"
                    onClick={() => { setShowActionsMenu(false); handleArchiveProperty(); }}
                    disabled={archiving}
                  >
                    {property.archived ? '📤 Unarchive' : '📦 Archive'}
                  </button>
                  <div className="property-actions-dropdown-divider" />
                  <button
                    className="property-actions-dropdown-item property-actions-dropdown-item--danger"
                    onClick={() => { setShowActionsMenu(false); setConfirmDelete(true); }}
                  >
                    Delete Property
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="delete-confirm" style={{ marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Delete property and all analyses?</span>
          <button className="btn btn-sm btn-secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleDeleteProperty}
            disabled={deleting}
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      )}

      {/* Affordability overview — visual range bars replacing the old price levels table */}
      {property && (
        <AffordabilityBarsCard
          askingPrice={property.askingPrice}
          baseInputs={baseInputs}
          cashOnHand={profile?.cashOnHand ?? 0}
          grossMonthlyIncome={profile ? (profile.grossAnnualIncome + (profile.coBorrower?.grossAnnualIncome ?? 0)) / 12 : 0}
          onOpenCalculator={handleOpenCalculator}
          onPlanOffer={handlePlanOffer}
        />
      )}

      {/* Analyses list — replaces the old single-line version summary */}
      <AnalysesList
        analyses={analyses}
        offers={offers}
        profileId={profileId!}
        propertyId={propertyId!}
        onPlanOffer={handlePlanOfferFromAnalysis}
        onDelete={deleteAnalysis}
      />

      {/* Stale profile warning — shown when profile was updated after the last analysis */}
      {isProfileStale && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.6rem 0.9rem',
          background: 'rgba(234,179,8,0.08)',
          border: '1px solid rgba(234,179,8,0.4)',
          borderRadius: '8px',
          fontSize: '0.8rem',
          margin: '0.25rem 0 0.75rem',
        }}>
          <span style={{ flexShrink: 0 }}>⚠️</span>
          <span style={{ flex: 1, color: 'var(--text-muted)' }}>
            Buyer profile updated since this analysis was saved — income, debts, and credit inputs may be outdated.
          </span>
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleOpenCalculator}
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Open Full Calculator
          </button>
        </div>
      )}

      {/* Scenario Comparison */}
      {scenarioRows.length > 0 && (
        <ScenarioComparisonCard
          rows={scenarioRows}
          onOpenCalculator={handleOpenCalculator}
          onAnalyzeWithProfile={handleAnalyzeWithProfile}
        />
      )}

      {/* Offers List */}
      <OffersList offers={offers} profileId={profileId!} propertyId={propertyId!} />
    </div>
  );
}

export function PropertyPage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Property Details</h2>
        <div className="note-box">
          Authentication is required to view property details.
          Configure <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable authentication.
        </div>
      </div>
    );
  }

  return <PropertyContent />;
}
